import React, { useState, useEffect } from "react";
import axios from "axios";
import "./SizeGuides.css";
import { useDispatch, useSelector } from "react-redux";
import {
  onSizeGuide,
  onSizeGuideAdd,
  onSizeGuideDelete,
  onSizeGuideEdit,
} from "../../redux/homeAction";

const SizeGuide = () => {
  const dispatch = useDispatch();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const getOnSizeGuideDelete = useSelector(
    (state) => state.home.getOnSizeGuideDelete
  );
  const getOnSizeGuideEdit = useSelector(
    (state) => state.home.getOnSizeGuideEdit
  );
  const getOnSizeGuideAdd = useSelector(
    (state) => state.home.getOnSizeGuideAdd
  );

  // form state for add / edit
  const emptyGuide = {
    category: "",
    brand: "Jayure",
    name: "",
    sizes: [
      {
        size: "",
        measurements: { chest: "", waist: "", hips: "", inseam: "" },
      },
    ],
  };

  const [form, setForm] = useState(emptyGuide);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    setError("");
    setLoading(true);
    dispatch(onSizeGuide(setLoading, setGuides));
  }, [getOnSizeGuideAdd, getOnSizeGuideEdit, getOnSizeGuideDelete]);

  function handleSizeChange(index, field, value) {
    const next = { ...form };
    if (field === "size") next.sizes[index].size = value;
    else next.sizes[index].measurements[field] = value;
    setForm(next);
  }

  function addSizeRow() {
    setForm((f) => ({
      ...f,
      sizes: [
        ...f.sizes,
        {
          size: "",
          measurements: { chest: "", waist: "", hips: "", inseam: "" },
        },
      ],
    }));
  }

  function removeSizeRow(i) {
    setForm((f) => ({ ...f, sizes: f.sizes.filter((_, idx) => idx !== i) }));
  }

  function handleFormChange(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function submitForm(e) {
    e.preventDefault();
    setError("");

    // basic validation
    if (!form.category.trim()) return setError("Category is required");
    if (!form.sizes.length) return setError("At least one size is required");

    try {
      if (editingId) {
        setLoading(true);
        dispatch(onSizeGuideEdit(setLoading, form, editingId));
      } else {
        setLoading(true);
        dispatch(onSizeGuideAdd(setLoading, form));
      }
      setForm(emptyGuide);
      setEditingId(null);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Save failed");
    }
  }

  function startEdit(guide) {
    // normalize numeric fields to strings for inputs
    const normalized = {
      ...guide,
      sizes: guide.sizes.map((s) => ({
        size: s.size || "",
        measurements: {
          chest: s.measurements?.chest ?? "",
          waist: s.measurements?.waist ?? "",
          hips: s.measurements?.hips ?? "",
          inseam: s.measurements?.inseam ?? "",
        },
      })),
    };
    setForm(normalized);
    setEditingId(guide._id || guide.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function removeGuide(id) {
    if (!window.confirm("Delete this size guide?")) return;
    try {
      setLoading(true);
      dispatch(onSizeGuideDelete(setLoading, id));
    } catch (err) {
      console.error(err);
      setError("Delete failed");
    }
  }

  return (
    <div className="container my-4 sizeguide-root">
      <div className="card shadow-sm">
        <div className="card-body">
          <h3 className="card-title">
            {editingId ? "Edit Size Guide" : "Create Size Guide"}
          </h3>

          <form onSubmit={submitForm} className="mb-3">
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row g-2">
              <div className="col-md-4">
                <label className="form-label">Category *</label>
                <input
                  className="form-control"
                  value={form.category}
                  onChange={(e) => handleFormChange("category", e.target.value)}
                  placeholder="e.g. T-shirt, Jeans"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Brand</label>
                <input
                  className="form-control"
                  value={form.brand}
                  onChange={(e) => handleFormChange("brand", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Name / Note</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                />
              </div>
            </div>

            <hr />

            <h5>Sizes</h5>
            {form.sizes.map((s, i) => (
              <div className="size-row p-2 mb-2 border rounded" key={i}>
                <div className="row align-items-end g-2">
                  <div className="col-md-2">
                    <label className="form-label">Size</label>
                    <input
                      className="form-control"
                      value={s.size}
                      onChange={(e) =>
                        handleSizeChange(i, "size", e.target.value)
                      }
                      placeholder="S / M / L"
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">Chest (cm)</label>
                    <input
                      className="form-control"
                      value={s.measurements.chest}
                      onChange={(e) =>
                        handleSizeChange(i, "chest", e.target.value)
                      }
                      inputMode="numeric"
                      placeholder="e.g. 90"
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">Waist (cm)</label>
                    <input
                      className="form-control"
                      value={s.measurements.waist}
                      onChange={(e) =>
                        handleSizeChange(i, "waist", e.target.value)
                      }
                      inputMode="numeric"
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">Hips (cm)</label>
                    <input
                      className="form-control"
                      value={s.measurements.hips}
                      onChange={(e) =>
                        handleSizeChange(i, "hips", e.target.value)
                      }
                      inputMode="numeric"
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">Inseam (cm)</label>
                    <input
                      className="form-control"
                      value={s.measurements.inseam}
                      onChange={(e) =>
                        handleSizeChange(i, "inseam", e.target.value)
                      }
                      inputMode="numeric"
                    />
                  </div>

                  <div className="col-md-2 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm me-2"
                      onClick={() => removeSizeRow(i)}
                    >
                      Remove
                    </button>
                    {i === form.sizes.length - 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={addSizeRow}
                      >
                        + Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                {editingId ? "Update Guide" : "Create Guide"}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setForm(emptyGuide);
                  setEditingId(null);
                }}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-4">
        <h4>Existing Size Guides</h4>
        {loading ? (
          <div className="text-muted">Loading...</div>
        ) : guides.length === 0 ? (
          <div className="alert alert-info">
            No size guides yet. Create one above.
          </div>
        ) : (
          <div className="row">
            {guides.map((g) => (
              <div className="col-md-6" key={g._id || g.id}>
                <div className="card mb-3 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h5 className="mb-1">
                          {g.category}{" "}
                          <small className="text-muted">{g.brand}</small>
                        </h5>
                        <p className="mb-1 small text-muted">{g.name}</p>
                      </div>
                      <div>
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => startEdit(g)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeGuide(g._id || g.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="table-responsive mt-3">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Size</th>
                            <th>Chest</th>
                            <th>Waist</th>
                            <th>Hips</th>
                            <th>Inseam</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.sizes.map((s, i) => (
                            <tr key={i}>
                              <td>{s.size}</td>
                              <td>{s.measurements?.chest ?? "—"}</td>
                              <td>{s.measurements?.waist ?? "—"}</td>
                              <td>{s.measurements?.hips ?? "—"}</td>
                              <td>{s.measurements?.inseam ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SizeGuide;
